import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";

import {
    ACL_CREATE_USER_URL,
    ACL_EDIT_USER_URL,
    ACL_GET_ROLES_LIST_URL,
    REGEX_USERNAME,
    REGEX_PASSWORD
} from "../../config";
import SubmitButton from "../button/SubmitButton";
import CancelButton from "../button/CancelButton";
import {
    showSuccessAlert,
    showErrorAlert,
} from "../../store/actions/alerts";

import TextField from "../formik/TextField";
import Select from "../formik/Select";

import makeApiRequest from "../../utils/axios/new-axios";
import history from "../../store/history";

const useStyles = makeStyles((theme) => ({
    form: {
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        width: "fit-content",
    },
}));

export default function AddEditUserForm({
    action,
    openModal,
    handleCloseModal,
    initialValues,
    userId,
}) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const [roles, setRoles] = useState([]);

    const yupSchema = Yup.object().shape({
        name: Yup.string().trim().required("Name Required!"),
        username: Yup.string().trim().required("Username Required!").matches(REGEX_USERNAME, "No spaces and invalid characters"),
        email: Yup.string().email("Invalid Email Address").trim().required("Email Required!"),
        role: Yup.number().min(1, "Invalid input").integer().required("Role Required"),
        ...(action === "add" && {
            password: Yup.string().trim().required("Password Required!").matches(REGEX_PASSWORD,
                "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character")
        }),
        ...(action === "edit" && { status: Yup.number().min(0).integer().required() }),
    });

    useEffect(() => {
        makeApiRequest({
            url: ACL_GET_ROLES_LIST_URL.url,
            method: ACL_GET_ROLES_LIST_URL.method,
        })
            .then((response) => {
                const rs = response.data.map(r => ({
                    value: r.id,
                    label: r.name
                }));
                setRoles(rs);
            })
            .catch((error) => {
                dispatch(
                    showErrorAlert(
                        "Roles Fetch Error : " +
                        (!!error.response
                            ? `${error.response.status}! - ${error.response.data.errorMessage}`
                            : error.message)
                    )
                );
            });
        // eslint-disable-next-line
    }, []); // [] is required to make sure that it runs only once

    async function handleSubmit(values, { setSubmitting }) {
        setSubmitting(true);
        const reqJson = {
            name: values.name.trim(),
            username: values.username.trim(),
            email: values.email.trim(),
            ...(values.password && { password: values.password.trim() }),
            ...(values.status >= 0 && { status: values.status }),
            role: values.role,
        };

        await makeApiRequest({
            url:
                action === "add"
                    ? ACL_CREATE_USER_URL.url
                    : `${ACL_EDIT_USER_URL.url}/${userId}`,
            method:
                action === "add"
                    ? ACL_CREATE_USER_URL.method
                    : ACL_EDIT_USER_URL.method,
            data: reqJson,
        })
            .then((response) => {
                dispatch(showSuccessAlert(`User ${reqJson.name} ${action === "add" ? "created" : "updated"} successfully`));
                history.push(`/acl/users`);
            })
            .catch((error) => {
                setSubmitting(false);
                dispatch(
                    showErrorAlert(
                        "Error : " +
                        (!!error.response
                            ? `${error.response.status}! - ${error.response.data.errorMessage}`
                            : error.message)
                    )
                );
            });

        setSubmitting(false);
    }

    return (
        <Dialog
            open={openModal}
            onClose={handleCloseModal}
            fullWidth={true}
            maxWidth="sm"
            aria-labelledby="user-dialog-add-edit"
            aria-describedby="user-dialog-add-edit"
            disableBackdropClick={true}
            disableEscapeKeyDown={true}
            disableEnforceFocus={true}
        >
            <DialogTitle id="user-dialog-title">
                {action === "add" ? "Add" : "Edit"} Admin User
            </DialogTitle>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={yupSchema}
            >
                {(props) => {
                    const { touched, errors, isSubmitting } = props;
                    return (
                        <Form className={classes.form}>
                            <DialogContent dividers style={{ overflow: "visible" }}>
                                <Field
                                    component={TextField}
                                    name="name"
                                    margin="dense"
                                    label="Name"
                                    fullWidth
                                    helperText={touched.name && errors.name && errors.name}
                                    error={!!touched.name && !!errors.name}
                                    disabled={isSubmitting}
                                    autoFocus
                                />
                                <Field
                                    component={TextField}
                                    name="username"
                                    margin="dense"
                                    label="Username"
                                    fullWidth
                                    helperText={touched.username && errors.username && errors.username}
                                    error={!!touched.username && !!errors.username}
                                    disabled={isSubmitting}
                                />
                                <Field
                                    component={TextField}
                                    name="email"
                                    margin="dense"
                                    label="Email"
                                    fullWidth
                                    helperText={touched.email && errors.email && errors.email}
                                    error={!!touched.email && !!errors.email}
                                    disabled={isSubmitting}
                                />
                                {action === "add" &&
                                    <Field
                                        component={TextField}
                                        name="password"
                                        margin="dense"
                                        label="Password"
                                        type="password"
                                        fullWidth
                                        helperText={touched.password && errors.password && errors.password}
                                        error={!!touched.password && !!errors.password}
                                        disabled={isSubmitting}
                                    />}
                                {action === "edit" &&
                                    <Field
                                        component={Select}
                                        name="status"
                                        label="Status"
                                        fullWidth
                                        options={[
                                            {
                                                value: 0,
                                                label: "In-Active",
                                            },
                                            {
                                                value: 1,
                                                label: "Active",
                                            },
                                            {
                                                value: 2,
                                                label: "Blocked",
                                            },
                                            {
                                                value: 3,
                                                label: "Locked Out",
                                            },
                                        ]}
                                        disabled={isSubmitting}
                                    />}
                                <Field
                                    component={Select}
                                    name="role"
                                    label="Role"
                                    fullWidth
                                    options={roles}
                                    disabled={isSubmitting}
                                />
                                <ErrorMessage name="role" >
                                    {msg => <div style={{ color: 'red' }}>{msg}</div>}
                                </ErrorMessage>
                            </DialogContent>
                            <DialogActions style={{ margin: "20px" }}>
                                <CancelButton
                                    onClick={handleCloseModal}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </CancelButton>
                                <SubmitButton
                                    disabled={isSubmitting}
                                    isSubmitting={isSubmitting}
                                    autoFocus
                                >
                                    {action === "add" ? "Add" : "Update"}
                                </SubmitButton>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    )
}
